import {
  Badge,
  Box,
  Button,
  Card,
  Code,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Spinner,
  Table,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EditIcon,
  EyeIcon,
  FileTextIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react'
import { useState } from 'react'
import {
  k8sApi,
  type ConfigMap,
  type CreateConfigMapRequest,
  type UpdateConfigMapRequest,
} from '~/lib/api'
import { toaster } from '~/toaster'

interface ConfigMapsTabProps {
  namespace: string
  isAdmin: boolean
}

const PAGE_SIZE = 10

// Key-Value Editor Component
function KeyValueEditor({
  data,
  onChange,
  readOnly = false,
}: {
  data: Record<string, string>
  onChange?: (data: Record<string, string>) => void
  readOnly?: boolean
}) {
  const entries = Object.entries(data)

  const handleKeyChange = (oldKey: string, newKey: string) => {
    if (!onChange || readOnly) return
    const newData = { ...data }
    const value = newData[oldKey]
    delete newData[oldKey]
    newData[newKey] = value
    onChange(newData)
  }

  const handleValueChange = (key: string, value: string) => {
    if (!onChange || readOnly) return
    onChange({ ...data, [key]: value })
  }

  const handleAddEntry = () => {
    if (!onChange || readOnly) return
    let newKey = 'new-key'
    let counter = 1
    while (data[newKey]) {
      newKey = `new-key-${counter++}`
    }
    onChange({ ...data, [newKey]: '' })
  }

  const handleRemoveEntry = (key: string) => {
    if (!onChange || readOnly) return
    const newData = { ...data }
    delete newData[key]
    onChange(newData)
  }

  return (
    <VStack align="stretch" gap={3}>
      {entries.length === 0 ? (
        <Text color="content.secondary" fontSize="sm" fontStyle="italic">
          No data entries
        </Text>
      ) : (
        entries.map(([key, value]) => (
          <HStack key={key} gap={2} align="start">
            <Input
              size="sm"
              fontFamily="mono"
              placeholder="Key"
              value={key}
              onChange={(e) => handleKeyChange(key, e.target.value)}
              readOnly={readOnly}
              w="200px"
              flexShrink={0}
            />
            <Textarea
              size="sm"
              fontFamily="mono"
              placeholder="Value"
              value={value}
              onChange={(e) => handleValueChange(key, e.target.value)}
              readOnly={readOnly}
              minH="36px"
              rows={1}
              resize="vertical"
            />
            {!readOnly && (
              <IconButton
                size="sm"
                variant="ghost"
                colorPalette="red"
                onClick={() => handleRemoveEntry(key)}
                aria-label="Remove entry"
              >
                <Trash2Icon size={14} />
              </IconButton>
            )}
          </HStack>
        ))
      )}
      {!readOnly && (
        <Button size="sm" variant="outline" onClick={handleAddEntry} alignSelf="flex-start">
          <PlusIcon size={14} />
          Add Entry
        </Button>
      )}
    </VStack>
  )
}

// View ConfigMap Modal
function ViewConfigMapModal({ configMap, onClose }: { configMap: ConfigMap; onClose: () => void }) {
  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="800px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">ConfigMap Details</Heading>
            <Text fontSize="sm" color="content.secondary">
              {configMap.namespace}/{configMap.name}
            </Text>
          </VStack>
          <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close dialog">
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 80px)" p={4}>
          <VStack align="stretch" gap={4}>
            <Box>
              <Text fontWeight="semibold" mb={2}>
                Metadata
              </Text>
              <HStack gap={4} flexWrap="wrap">
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Name
                  </Text>
                  <Code>{configMap.name}</Code>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Namespace
                  </Text>
                  <Code>{configMap.namespace}</Code>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Age
                  </Text>
                  <Text>{configMap.age}</Text>
                </Box>
              </HStack>
            </Box>

            {configMap.labels && Object.keys(configMap.labels).length > 0 && (
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Labels
                </Text>
                <HStack gap={2} flexWrap="wrap">
                  {Object.entries(configMap.labels).map(([k, v]) => (
                    <Badge key={k} variant="outline" fontFamily="mono">
                      {k}={v}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            )}

            <Box>
              <Text fontWeight="semibold" mb={2}>
                Data ({Object.keys(configMap.data).length} keys)
              </Text>
              <KeyValueEditor data={configMap.data} readOnly />
            </Box>
          </VStack>
        </Box>
      </Box>
    </Box>
  )
}

// Create/Edit ConfigMap Modal
function ConfigMapFormModal({
  configMap,
  namespace,
  onClose,
  onSuccess,
}: {
  configMap?: ConfigMap
  namespace: string
  onClose: () => void
  onSuccess: () => void
}) {
  const isEdit = !!configMap
  const [name, setName] = useState(configMap?.name || '')
  const [data, setData] = useState<Record<string, string>>(configMap?.data || {})
  const [labels, setLabels] = useState<Record<string, string>>(configMap?.labels || {})
  const [showLabels, setShowLabels] = useState(
    configMap?.labels && Object.keys(configMap.labels).length > 0,
  )

  const createMutation = useMutation({
    mutationFn: (req: CreateConfigMapRequest) => k8sApi.createConfigMap(namespace, req),
    onSuccess: () => {
      toaster.create({
        title: 'ConfigMap created',
        description: `ConfigMap "${name}" created successfully`,
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to create ConfigMap',
        description: error.message,
        type: 'error',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (req: UpdateConfigMapRequest) =>
      k8sApi.updateConfigMap(configMap!.namespace, configMap!.name, req),
    onSuccess: () => {
      toaster.create({
        title: 'ConfigMap updated',
        description: `ConfigMap "${configMap!.name}" updated successfully`,
        type: 'success',
      })
      onSuccess()
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to update ConfigMap',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = () => {
    if (isEdit) {
      updateMutation.mutate({
        data,
        labels: showLabels ? labels : undefined,
      })
    } else {
      if (!name.trim()) {
        toaster.create({
          title: 'Name required',
          description: 'Please enter a name for the ConfigMap',
          type: 'error',
        })
        return
      }
      createMutation.mutate({
        name: name.trim(),
        data,
        labels: showLabels ? labels : undefined,
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="800px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">{isEdit ? 'Edit' : 'Create'} ConfigMap</Heading>
            <Text fontSize="sm" color="content.secondary">
              {isEdit ? `${configMap.namespace}/${configMap.name}` : `Namespace: ${namespace}`}
            </Text>
          </VStack>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            disabled={isPending}
            aria-label="Close dialog"
          >
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 140px)" p={4}>
          <VStack align="stretch" gap={4}>
            {!isEdit && (
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Name *
                </Text>
                <Input
                  placeholder="my-config"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fontFamily="mono"
                />
              </Box>
            )}

            <Box>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="semibold">Data</Text>
              </Flex>
              <KeyValueEditor data={data} onChange={setData} />
            </Box>

            <Box>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontWeight="semibold">Labels</Text>
                <Button size="xs" variant="ghost" onClick={() => setShowLabels(!showLabels)}>
                  {showLabels ? 'Hide' : 'Show'} Labels
                </Button>
              </Flex>
              {showLabels && <KeyValueEditor data={labels} onChange={setLabels} />}
            </Box>
          </VStack>
        </Box>
        <Flex justify="flex-end" gap={2} p={4} borderTopWidth="1px" borderColor="border">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button colorPalette="blue" onClick={handleSubmit} loading={isPending}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export function ConfigMapsTab({ namespace, isAdmin }: ConfigMapsTabProps) {
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewConfigMap, setViewConfigMap] = useState<ConfigMap | null>(null)
  const [editConfigMap, setEditConfigMap] = useState<ConfigMap | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)

  // Fetch configmaps
  const {
    data: configMapsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['configmaps', namespace],
    queryFn: () => k8sApi.configMaps(namespace || undefined),
    retry: false,
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ({ namespace, name }: { namespace: string; name: string }) =>
      k8sApi.deleteConfigMap(namespace, name),
    onSuccess: (_, variables) => {
      toaster.create({
        title: 'ConfigMap deleted',
        description: `ConfigMap "${variables.name}" deleted successfully`,
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['configmaps'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete ConfigMap',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleDelete = (namespace: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete ConfigMap "${name}"?\n\nThis action cannot be undone.`,
      )
    ) {
      deleteMutation.mutate({ namespace, name })
    }
  }

  const allConfigMaps = configMapsData?.configMaps || []

  // Filter based on search query
  const filteredConfigMaps = allConfigMaps.filter((cm) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      cm.name.toLowerCase().includes(query) ||
      cm.namespace.toLowerCase().includes(query) ||
      Object.keys(cm.data).some((k) => k.toLowerCase().includes(query))
    )
  })

  // Pagination
  const totalPages = Math.ceil(filteredConfigMaps.length / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const endIndex = startIndex + PAGE_SIZE
  const configMaps = filteredConfigMaps.slice(startIndex, endIndex)

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setCurrentPage(1)
  }

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['configmaps'] })
  }

  return (
    <VStack align="stretch" gap={4}>
      <Flex justify="space-between" align="center">
        <Box position="relative" w="300px">
          <HStack
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            zIndex={1}
            pointerEvents="none"
          >
            <SearchIcon size={16} color="gray" />
          </HStack>
          <Input
            size="sm"
            pl={10}
            pr={searchQuery ? 10 : 3}
            placeholder="Search by name, namespace, or key..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <Button
              size="xs"
              variant="ghost"
              position="absolute"
              right={1}
              top="50%"
              transform="translateY(-50%)"
              onClick={clearSearch}
              aria-label="Clear search"
              minW="auto"
              h="auto"
              p={1}
            >
              <XIcon size={14} />
            </Button>
          )}
        </Box>
        <HStack gap={2}>
          {isAdmin && namespace && (
            <Button size="sm" colorPalette="blue" onClick={() => setShowCreateModal(true)}>
              <PlusIcon size={16} />
              Create
            </Button>
          )}
          <Button size="sm" onClick={() => refetch()}>
            <RefreshCwIcon size={16} />
            Refresh
          </Button>
        </HStack>
      </Flex>

      <Card.Root>
        <Table.Root>
          <Table.Header>
            <Table.Row bg="bg.subtle">
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Namespace</Table.ColumnHeader>
              <Table.ColumnHeader>Keys</Table.ColumnHeader>
              <Table.ColumnHeader>Age</Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={5}>
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ) : configMaps.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={5}>
                  <VStack py={8} gap={2}>
                    <FileTextIcon size={32} color="gray" />
                    <Text textAlign="center" color="content.secondary">
                      {searchQuery
                        ? `No ConfigMaps matching "${searchQuery}"`
                        : namespace
                          ? 'No ConfigMaps in this namespace'
                          : 'No ConfigMaps found'}
                    </Text>
                    {searchQuery && (
                      <Button size="sm" variant="ghost" onClick={clearSearch}>
                        Clear search
                      </Button>
                    )}
                  </VStack>
                </Table.Cell>
              </Table.Row>
            ) : (
              configMaps.map((cm) => (
                <Table.Row key={`${cm.namespace}/${cm.name}`}>
                  <Table.Cell>
                    <Text fontWeight="medium">{cm.name}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="outline">{cm.namespace}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette="blue">{Object.keys(cm.data).length}</Badge>
                  </Table.Cell>
                  <Table.Cell>{cm.age}</Table.Cell>
                  <Table.Cell>
                    <HStack gap={2}>
                      <Button size="xs" variant="ghost" onClick={() => setViewConfigMap(cm)}>
                        <EyeIcon size={14} />
                        View
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="blue"
                            onClick={() => setEditConfigMap(cm)}
                          >
                            <EditIcon size={14} />
                            Edit
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            colorPalette="red"
                            loading={deleteMutation.isPending}
                            onClick={() => handleDelete(cm.namespace, cm.name)}
                          >
                            <Trash2Icon size={14} />
                            Delete
                          </Button>
                        </>
                      )}
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="space-between" align="center" pt={2}>
          <Text fontSize="sm" color="content.secondary">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredConfigMaps.length)} of{' '}
            {filteredConfigMaps.length} ConfigMaps
            {searchQuery && ` (filtered from ${allConfigMaps.length})`}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon size={16} />
              Previous
            </Button>
            <HStack gap={1}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page: number
                if (totalPages <= 5) {
                  page = i + 1
                } else if (currentPage <= 3) {
                  page = i + 1
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i
                } else {
                  page = currentPage - 2 + i
                }
                return (
                  <Button
                    key={page}
                    size="sm"
                    variant={page === currentPage ? 'solid' : 'outline'}
                    colorPalette={page === currentPage ? 'blue' : undefined}
                    onClick={() => setCurrentPage(page)}
                    minW="40px"
                  >
                    {page}
                  </Button>
                )
              })}
            </HStack>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRightIcon size={16} />
            </Button>
          </HStack>
        </Flex>
      )}

      {/* View Modal */}
      {viewConfigMap && (
        <ViewConfigMapModal configMap={viewConfigMap} onClose={() => setViewConfigMap(null)} />
      )}

      {/* Edit Modal */}
      {editConfigMap && (
        <ConfigMapFormModal
          configMap={editConfigMap}
          namespace={editConfigMap.namespace}
          onClose={() => setEditConfigMap(null)}
          onSuccess={handleSuccess}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && namespace && (
        <ConfigMapFormModal
          namespace={namespace}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </VStack>
  )
}
